import { UNSPACED_SCRIPT } from "@/lib/vocabulary-tokenize";
import type { ChatVocabularyEntry } from "./types";

// Splits a message body into the runs the bubble renders: plain text, and the
// words a flashcard capture was made from (underlined, hover = state).
//
// Rules (spec 2026-09-06 § In chat, #825 phase 2):
// - one match per entry: its first LITERAL occurrence, else — where the
//   tokenizer collapsed the separator the body holds (see patternsFor) — the
//   first TOLERANT one. Both are compared case-insensitively on NFC-normalized
//   text (the server stores the NFC form; a body typed with a decomposed
//   accent still matches);
// - longest entry first, so "thank you" claims its span before "you" looks for
//   a free one; spans never overlap — a shorter entry whose first occurrence
//   is taken keeps scanning for the next free one;
// - anything unmatched passes through untouched, in order.
//
// The output concatenates back to the NFC form of the body. Pure, no React.
export interface SavedWordSegment {
  text: string;
  entry: ChatVocabularyEntry | null;
}

interface Span {
  start: number;
  end: number;
  entry: ChatVocabularyEntry;
}

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// The patterns tried, in order, for one entry.
//
// A capture's sourceText is what the tokenizer read the selection back as, and
// that read-back drops whatever punctuation or newline sat between two
// selected words (separatorFor in vocabulary-tokenize.ts). Which is dropped
// how depends on the script: two space-delimited words are rejoined with a
// SINGLE SPACE ("gracias, amigo" is stored "gracias amigo", "hola\nmundo" as
// "hola mundo"), while two adjacent Han/Kana words are rejoined with NOTHING
// ("你好，世界" is stored "你好世界"). Literal matching alone can underline
// neither of them in their own message.
//
// The literal form still goes first — where the body does hold the capture
// verbatim, that occurrence is the one to claim — with a tolerant pattern
// behind it that puts the dropped separator back. The two join kinds want
// different gaps:
//
// - a SPACE in sourceText only ever stands for something the body really held
//   (separatorFor returns "" when the two words were adjacent), so that
//   junction requires at least one separator character. With `*` the capture
//   "hola mundo" would wrongly underline the unrelated token "holamundo".
// - an UNSPACED junction — between two adjacent Han/Kana characters — stands
//   for either nothing or the dropped punctuation, so it takes `*`.
//
// A single-word Latin entry produces the same pattern twice, so it runs once.
// \p{N} is in the class because tokenizeSpaced's SPACED_WORD matches letters
// only, so a digit between two words is dropped from sourceText exactly like
// punctuation is ("hola 2 mundo" is stored "hola mundo"). \p{L} stays OUT: the
// gap must never leap over an intervening word. U+200B is in the class because
// Thai, Khmer and Burmese text often carries the zero-width space as an
// invisible word separator (#768): it is Cf, so neither \s nor \p{P}, yet the
// tokenizer drops it like punctuation and stores "สวัสดี<U+200B>ครับ" as
// "สวัสดีครับ".
const SEPARATOR = "[\\s\\u200B\\p{P}\\p{S}\\p{N}]";

// One whitespace-delimited word, with a `*` gap at every boundary between two
// adjacent unspaced-script characters.
function unspacedTolerantSource(word: string): string {
  const chars = [...word];
  return chars
    .map((char, i) => {
      const previous = chars[i - 1];
      const unspacedBoundary =
        previous !== undefined && UNSPACED_SCRIPT.test(previous) && UNSPACED_SCRIPT.test(char);
      return (unspacedBoundary ? `${SEPARATOR}*` : "") + escapeRegExp(char);
    })
    .join("");
}

// A capture is a whole word, so a spaced-script edge must sit on a word
// boundary: without it a short function word — the most common beginner
// capture — was anchored to its first SUBSTRING occurrence, usually inside an
// unrelated longer word ("en" in "Tienes", "the" in "them"). A letter or a
// combining mark right before or after the match means it cut a word. Han /
// Kana edges stay unbounded: those scripts have no boundaries to test and
// "日本語" must keep matching inside "は日本語を".
function wordBounded(source: string, sourceText: string): string {
  const chars = [...sourceText];
  const before = UNSPACED_SCRIPT.test(chars[0] ?? "") ? "" : "(?<![\\p{L}\\p{M}\\u200C\\u200D])";
  const after = UNSPACED_SCRIPT.test(chars[chars.length - 1] ?? "") ? "" : "(?![\\p{L}\\p{M}\\u200C\\u200D])";
  return before + source + after;
}

function patternsFor(sourceText: string): RegExp[] {
  const literal = escapeRegExp(sourceText);
  const tolerant = sourceText
    .split(/\s+/)
    .filter((w) => w.length > 0)
    .map(unspacedTolerantSource)
    .join(`${SEPARATOR}+`);
  const sources = tolerant === literal ? [literal] : [literal, tolerant];
  // The `iu` flags fold case per Unicode without changing indices — unlike
  // toLowerCase(), which can change the string length ("İ").
  return sources.map((source) => new RegExp(wordBounded(source, sourceText), "giu"));
}

// The first occurrence of `re` in `text` that no span has claimed, or null.
function firstFreeMatch(
  re: RegExp,
  text: string,
  taken: (start: number, end: number) => boolean,
): { start: number; end: number } | null {
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    const start = match.index;
    const end = start + match[0].length;
    if (!taken(start, end)) return { start, end };
    re.lastIndex = start + 1;
  }
  return null;
}

export function segmentSavedWords(body: string, entries: ChatVocabularyEntry[]): SavedWordSegment[] {
  const text = body.normalize("NFC");
  if (text.length === 0) return [];
  const candidates = entries
    .filter((e) => e.sourceText.trim().length > 0)
    .sort((a, b) => b.sourceText.length - a.sourceText.length);
  if (candidates.length === 0) return [{ text, entry: null }];

  const spans: Span[] = [];
  const overlaps = (start: number, end: number) => spans.some((s) => start < s.end && end > s.start);

  for (const entry of candidates) {
    for (const re of patternsFor(entry.sourceText.normalize("NFC"))) {
      const found = firstFreeMatch(re, text, overlaps);
      if (found) {
        spans.push({ ...found, entry });
        break;
      }
    }
  }

  spans.sort((a, b) => a.start - b.start);
  const out: SavedWordSegment[] = [];
  let cursor = 0;
  for (const span of spans) {
    if (span.start > cursor) out.push({ text: text.slice(cursor, span.start), entry: null });
    out.push({ text: text.slice(span.start, span.end), entry: span.entry });
    cursor = span.end;
  }
  if (cursor < text.length) out.push({ text: text.slice(cursor), entry: null });
  return out;
}
